import * as yup from 'yup'

import allowedValues from './allowed.values.js'
import {
  requiredBool,
  string,
  requiredString,
  requiredInteger,
  requiredPrice,
  requiredNumber,
  requiredPercentage,
  state,
  states
} from '../yup/schemas.js'

import {
  VALIDATION, JOB_STATUSES, JOBS_BOARD_TYPES,
  PAY_STRUCTURE, JOB_LOCATION_TYPE, PRIOR_EXPERIENCE_NEEDED
} from '../constants.js'
import { object } from 'yup'

const { MESSAGES, REGEXP } = VALIDATION

export const jobSchema = yup.object({
  id: requiredString.uuid(),
  groupId: requiredString.uuid(),
  status: requiredString.oneOf(Object.values(JOB_STATUSES)),
  jobsBoardType: requiredString.oneOf(Object.values(JOBS_BOARD_TYPES)).defaultIfRequired(JOBS_BOARD_TYPES.ENTRY),

  autoRenew: yup.boolean().optional(),

  position: yup.string()
    .when('jobLocationType', {
      is: JOB_LOCATION_TYPE.LOCAL,
      then: requiredString.oneOf(allowedValues.positionLocal)
    })
    .when('jobLocationType', {
      is: JOB_LOCATION_TYPE.REGIONAL,
      then: requiredString.oneOf(allowedValues.positionRegional)
    })
    .when('jobLocationType', {
      is: JOB_LOCATION_TYPE.OVER_THE_ROAD,
      then: requiredString.oneOf(allowedValues.positionOTR)
    })
    .when('jobLocationType', {
      is: JOB_LOCATION_TYPE.TEAM_DRIVING,
      then: requiredString.oneOf(allowedValues.positionTeamDriving)
    }),
  otherPositionName: yup.string()
    .when('position', {
      is: 'Other',
      then: requiredString
  }),
  reportingLocation: yup.object({
    city: requiredString,
    zip: requiredString.zip(),
    state: state.required(),
    requiredLicenseType: requiredString.oneOf(allowedValues.requiredLicenseType),
    endorsements: yup.object({
      doublesOrTriples: requiredString.oneOf(allowedValues.endorsementsDoublesOrTriples),
      tanker: requiredString.oneOf(allowedValues.endorsementsTanker),
      hazardousMaterials: requiredString.oneOf(allowedValues.endorsementsHazardousMaterials),
      manualTransmissionRestriction: requiredBool
    }).objectRequired()
  }).objectRequired(),
  priorExperienceNeeded: requiredString,
  yearsOfExperienceNeeded: requiredInteger
    .min(0, VALIDATION.MESSAGES.MIN(0))
    .max(30, VALIDATION.MESSAGES.MAX(30))
    .existsWhen('priorExperienceNeeded', PRIOR_EXPERIENCE_NEEDED.MORE_THAN_SCHOOL),

  contactPersonName: requiredString,
  contactPersonPhoneNumber: requiredString.matches(REGEXP.PHONE, MESSAGES.PHONE),

  TWICCardRequired: requiredBool,

  minimumAge: requiredInteger
    .min(18, VALIDATION.MESSAGES.MIN(18))
    .max(25, VALIDATION.MESSAGES.MAX(25)),

  tuitionReimbursement: requiredBool,

  signOnBonus: requiredBool,
  signOnBonusMaxAmount: requiredPrice.existsWhen('signOnBonus', true),
  signOnBonusPayablePeriod: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).max(36, VALIDATION.MESSAGES.MAX(36)).existsWhen('signOnBonus', true),

  jobLocationType: requiredString.oneOf(allowedValues.jobLocationType),

  localJobOptions: yup.object({
    fullTime: requiredBool,
    yearRoundWork: requiredBool,

    payStructure: requiredString.oneOf(allowedValues.payStructure),
    hourlyPayOptions: yup.object({
      entry: yup.object({
        beginnersPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange()
      }),
      experienced: yup.object({
        beginnersPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange()
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.HOURLY).objectRequired(),
    mileagePayOptions: yup.object({
      entry: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      })
    }).existsWhen('payStructure', PAY_STRUCTURE.MILEAGE).objectRequired(),
    percentageOfLoadPayOptions: yup.object({
      entry: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.PERCENTAGE_OF_LOAD).objectRequired(),

    numberOfHoursPerWeek: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).max(100, VALIDATION.MESSAGES.MAX(100)),

    mandatoryOvertime: requiredBool,
    overtimeAfter8HoursDay: requiredBool,
    overtimeAfter40HoursWeek: requiredBool,

    workDaysVary: requiredBool,
    workDays: yup.array().of(yup.string().oneOf(allowedValues.workDays)).notEmpty().required(),

    workHoursVary: requiredBool,
    workHoursType: requiredString.oneOf(allowedValues.workHours),

    physicalRequirements: requiredString.oneOf(allowedValues.physicalRequirements),
    requiredLoadUnload: requiredBool,
    drivingPercentage: requiredNumber
  }).existsWhen('jobLocationType', JOB_LOCATION_TYPE.LOCAL).objectRequired(),

  regionalJobOptions: yup.object({
    fullTime: requiredBool,

    yearRoundWork: requiredBool,

    payStructure: requiredString.oneOf(allowedValues.payStructure),
    hourlyPayOptions: yup.object({
      entry: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),
      experienced: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),

      numberOfHoursPerWeek: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).max(100, VALIDATION.MESSAGES.MAX(100)),
      mandatoryOvertime: requiredBool,
      overtimeAfter8HoursDay: requiredBool,
      overtimeAfter40HoursWeek: requiredBool
    }).existsWhen('payStructure', PAY_STRUCTURE.HOURLY).objectRequired(),
    mileagePayOptions: yup.object({
      entry: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.MILEAGE).objectRequired(),
    percentageOfLoadPayOptions: yup.object({
      entry: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.PERCENTAGE_OF_LOAD).objectRequired(),

    multiStopPay: requiredBool,
    layoverPay: requiredBool,
    breakdownPay: requiredBool,
    payPerDiem: requiredBool,
    dedicatedRoute: requiredBool,
    physicalRequirements: requiredString.oneOf(allowedValues.physicalRequirements),
    percentDropAndHook: requiredPercentage.min(0, VALIDATION.MESSAGES.MIN(0)).max(100, VALIDATION.MESSAGES.MAX(100)),
    numberOfNightsAway: requiredInteger,
    numberOfConsecutiveNightsAwayPerWeek: requiredInteger,
    restPeriodWithSleeperBerths: requiredBool,
    companyProvidesMotels: requiredBool.existsWhen('restPeriodWithSleeperBerths', false),
    drivingStates: states.required(),
    drivingPercentage: requiredNumber
  }).existsWhen('jobLocationType', JOB_LOCATION_TYPE.REGIONAL).objectRequired(),

  OTRJobOptions: yup.object({
    fullTime: requiredBool,

    yearRoundWork: requiredBool,

    payStructure: requiredString.oneOf(allowedValues.payStructure),
    hourlyPayOptions: yup.object({
      entry: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),
      experienced: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),

      numberOfHoursPerWeek: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).max(100, VALIDATION.MESSAGES.MAX(100)),
      mandatoryOvertime: requiredBool,
      overtimeAfter8HoursDay: requiredBool,
      overtimeAfter40HoursWeek: requiredBool
    }).existsWhen('payStructure', PAY_STRUCTURE.HOURLY).objectRequired(),
    mileagePayOptions: yup.object({
      entry: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.MILEAGE).objectRequired(),
    percentageOfLoadPayOptions: yup.object({
      entry: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.PERCENTAGE_OF_LOAD).objectRequired(),

    multiStopPay: requiredBool,
    layoverPay: requiredBool,
    detentionPay: requiredBool,
    breakdownPay: requiredBool,
    payPerDiem: requiredBool,
    dedicatedRoute: requiredBool,
    physicalRequirements: requiredString.oneOf(allowedValues.physicalRequirements),
    percentDropAndHook: requiredPercentage.min(0, VALIDATION.MESSAGES.MIN(0)).max(100, VALIDATION.MESSAGES.MAX(100)),
    numberOfNightsAway: requiredInteger,
    avarageTimeAtHome: requiredInteger,
    restPeriodWithSleeperBerths: requiredBool,
    companyProvidesMotels: requiredBool.existsWhen('restPeriodWithSleeperBerths', false),
    drivingStates: states.required(),
    drivingPercentage: requiredNumber.min(0, VALIDATION.MESSAGES.MIN(0)).max(100, VALIDATION.MESSAGES.MAX(100))
  }).existsWhen('jobLocationType', JOB_LOCATION_TYPE.OVER_THE_ROAD).objectRequired(),

  teamDrivingJobOptions: yup.object({
    fullTime: requiredBool,

    yearRoundWork: requiredBool,

    payStructure: requiredString.oneOf(allowedValues.payStructure),
    hourlyPayOptions: yup.object({
      entry: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),
      experienced: yup.object({
        startingPay: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
      }),

      numberOfHoursPerWeek: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).max(100, VALIDATION.MESSAGES.MAX(100)),
      mandatoryOvertime: requiredBool,
      overtimeAfter8HoursDay: requiredBool,
      overtimeAfter40HoursWeek: requiredBool
    }).existsWhen('payStructure', PAY_STRUCTURE.HOURLY).objectRequired(),
    mileagePayOptions: yup.object({
      entry: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        startingRatePerMile: yup.array().length(2, 'Required').of(requiredPrice).priceInputRange(),
        avarageMilesPerWeek: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.MILEAGE).objectRequired(),

    percentageOfLoadPayOptions: yup.object({
      entry: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
      experienced: yup.object({
        driversPayPercentage: requiredInteger
          .min(0, VALIDATION.MESSAGES.MIN(0))
          .max(100, VALIDATION.MESSAGES.MAX(100)),
        driversExpectedPay: requiredInteger
          .min(100, VALIDATION.MESSAGES.MIN(100))
          .max(10000, VALIDATION.MESSAGES.MAX(10000))
      }),
    }).existsWhen('payStructure', PAY_STRUCTURE.PERCENTAGE_OF_LOAD).objectRequired(),

    multiStopPay: requiredBool,
    layoverPay: requiredBool,
    detentionPay: requiredBool,
    breakdownPay: requiredBool,
    payPerDiem: requiredBool,
    dedicatedRoute: requiredBool,
    physicalRequirements: requiredString.oneOf(allowedValues.physicalRequirements),
    percentDropAndHook: requiredPercentage.min(0, VALIDATION.MESSAGES.MIN(0)).max(100, VALIDATION.MESSAGES.MAX(100)),
    numberOfNightsAway: requiredInteger,
    avarageTimeAtHome: requiredInteger,
    restPeriodWithSleeperBerths: requiredBool,
    companyProvidesMotels: requiredBool.existsWhen('restPeriodWithSleeperBerths', false),
    drivingStates: states.required(),
    drivingPercentage: requiredNumber.min(0, VALIDATION.MESSAGES.MIN(0)).max(100, VALIDATION.MESSAGES.MAX(100))
  }).existsWhen('jobLocationType', JOB_LOCATION_TYPE.TEAM_DRIVING).objectRequired()
}).objectRequired()

export const jobLocationTypes = yup.array().of(jobSchema.get('jobLocationType'))
  .notEmpty().unique()
export const statuses = yup.array().of(jobSchema.get('status'))
  .notEmpty().unique()

export const positionAll = string.oneOf([
  ...new Set([...allowedValues.positionLocal, ...allowedValues.positionRegional, ...allowedValues.positionOTR, ...allowedValues.positionTeamDriving])
])
export const positionsAll = yup.array().of(positionAll)
  .notEmpty().unique()
export const requiredLicenseTypeAll = yup.array().of(jobSchema.get('reportingLocation.requiredLicenseType'))
  .notEmpty().unique()
export const physicalRequirementsAll = yup.array().of(jobSchema.get('localJobOptions').fields.physicalRequirements)
  .notEmpty().unique()
export const payStructureAll = yup.array().of(jobSchema.get('localJobOptions').fields.payStructure)
  .notEmpty().unique()
export const doublesOrTriplesAll = yup.array().of(jobSchema.get('reportingLocation.endorsements.doublesOrTriples'))
  .notEmpty().unique()
export const tankerAll = yup.array().of(jobSchema.get('reportingLocation.endorsements.tanker'))
  .notEmpty().unique()
export const hazardousMaterialsAll = yup.array().of(jobSchema.get('reportingLocation.endorsements.hazardousMaterials'))
  .notEmpty().unique()
